import * as React from 'react';
import { ChangeEventHandler, MouseEventHandler, useState } from 'react';
import cn from 'classnames';
import { Paragraph } from '@mos-cat/typography';

import RatingStar from './inner/RatingStar/RatingStar';
import Form from './inner/Form/Form';
import { isTouchDevice } from './inner/helpers';

import '@mos-utils/css/lib/typography/Link.css';
import '@mos-utils/css/lib/typography/Button.css';
import './styles.pcss';

/**
 * Набор свойств для Feedback.
 * Интерфейс свойств рекомендуется экспортировать, чтобы проще было модифицировать поля.
 */
export interface Props {
  /**
   * Колбек на отправку отзыва и на оценки
   */
  onSubmit: (params: { rate: number; comment: string }) => boolean;
  /**
   * Заголовок для формы.
   */
  title?: string | JSX.Element;
}

/**
 * @param props
 * @constructor
 */

interface FeedbackState {
  rating: number;
  ratingHighlighted: number;
  formSubmitted: boolean;
  ratingError: boolean;
  formCommentary: string;
}

const ratingTexts: string[] = [
  'Ужасно',
  'Плохо',
  'Удовлетворительно',
  'Хорошо',
  'Отлично',
];

const ratingNumbers: number[] = [1, 2, 3, 4, 5];

function Feedback({ onSubmit, title }: Props) {
  const [state, setState] = useState<FeedbackState>({
    formSubmitted: false,
    ratingError: false,
    formCommentary: '',
    rating: 0,
    ratingHighlighted: 0,
  });

  const {
    formSubmitted,
    formCommentary,
    ratingError,
    rating,
    ratingHighlighted,
  } = state;

  const ratingTextClassNames = cn('MosFeedback__rating-text', {
    'MosFeedback__rating-text_hidden': !ratingHighlighted && !isTouchDevice(),
  });

  const thankMessageText =
    formSubmitted && formCommentary
      ? 'Спасибо за вашу оценку и комментарий!'
      : 'Спасибо за вашу оценку!';

  const currentRatingText = ratingTexts[(ratingHighlighted || rating) - 1];

  /* Rating star event handlers */

  const handleClickRatingStar = (newRating: number): void => {
    setState({
      ...state,
      rating: newRating,
      ratingError: false,
    });
  };

  const handleMouseEnterRatingStar = (newRatingHighlighted: number): void => {
    setState({
      ...state,
      ratingHighlighted: newRatingHighlighted,
    });
  };

  const handleMouseLeaveRatingStar = (): void => {
    setState({
      ...state,
      ratingHighlighted: 0,
    });
  };

  /* Form event handlers */

  const handleChangeFormCommentary: ChangeEventHandler<HTMLTextAreaElement> = ({
    target: { value },
  }) =>
    setState({
      ...state,
      formCommentary: value,
    });

  const handleClickSubmitFormButton: MouseEventHandler = () => {
    if (!rating) {
      setState({
        ...state,
        ratingError: true,
      });
      return;
    }
    if (onSubmit({ rate: rating, comment: formCommentary })) {
      setState({
        ...state,
        formSubmitted: true,
      });
    }
  };

  return (
    <div className="MosFeedback">
      <div className="MosFeedback__title">{title}</div>
      {!formSubmitted && (
        <>
          {ratingError && (
            <Paragraph className="MosFeedback__error">
              Выберите, какую оценку вы хотите поставить
            </Paragraph>
          )}
          <div className="MosFeedback__rating">
            {ratingNumbers.map((value) => (
              <RatingStar
                value={value}
                key={value}
                onClick={handleClickRatingStar}
                onMouseEnter={handleMouseEnterRatingStar}
                onMouseLeave={handleMouseLeaveRatingStar}
                ratingHighlighted={ratingHighlighted}
                rating={rating}
                className="MosFeedback__star"
              />
            ))}
          </div>
          <div className={ratingTextClassNames}>{currentRatingText}</div>
          <Paragraph>Добавьте комментарий по желанию</Paragraph>
          <Form
            commentary={formCommentary}
            onChange={handleChangeFormCommentary}
            onSubmit={handleClickSubmitFormButton}
            className="MosFeedback__form"
          />
        </>
      )}
      {formSubmitted && (
        <div className="MosFeedback__thanks-message">{thankMessageText}</div>
      )}
    </div>
  );
}

Feedback.defaultProps = {
  title: 'Оцените удобство сервиса',
};

export default Feedback;
